/**
 * 智能客服 - 访客会话 接口定义
 */
import { apiGet, apiGetWithLongIntegers, apiPost, apiPut } from '@/utils/request';
import {
  parseListResponse,
  parsePaginatedResponse,
  parsePaginatedResponseFull,
} from '@/utils/apiResponse';
import { DEFAULT_PAGE_SIZE, LARGE_PAGE_SIZE } from '@/constants/api';
import type { RecallMessageResponse } from '@/types/visitorSessions';
import type { BatchUpdateSessionsParams, BatchUpdateSessionsResult } from '@/types/chatRecords';
import type {
  AgentBoardAgentsResponse,
  AgentBoardKfId,
  AgentBoardSessionsResponse,
  SessionTransferPayload,
  SessionTransferResult,
  TransferAgent,
} from '@/types/workbench';
import { MOCK_SESSIONS, MOCK_MESSAGES, MOCK_SESSION_IDS } from '@/mock/chatRecords';

const useMock = import.meta.env.DEV && import.meta.env.VITE_MOCK_CHAT_RECORDS === 'true';

const isMockSession = (id: number) => (MOCK_SESSION_IDS as readonly number[]).includes(id);

export const sessionsApi = {
  list: (params: Record<string, unknown> = {}) =>
    apiGetWithLongIntegers('/sessions', {
      page: 1,
      per_page: DEFAULT_PAGE_SIZE,
      ...params,
    }).then((d) => parsePaginatedResponseFull(d, { page: 1, per_page: DEFAULT_PAGE_SIZE })),
  show: (id: number) => {
    if (useMock && isMockSession(id)) {
      const session = MOCK_SESSIONS[id];
      return Promise.resolve(session ?? { user: null });
    }
    return apiGetWithLongIntegers(`/sessions/${id}`);
  },
  messages: (id: number, params: Record<string, unknown> = {}) => {
    if (useMock && isMockSession(id)) {
      const list = MOCK_MESSAGES[id] ?? [];
      return Promise.resolve({ list: [...list], total: list.length });
    }
    return apiGet(`/sessions/${id}/messages`, {
      page: 1,
      per_page: LARGE_PAGE_SIZE,
      ...params,
    }).then((d) => parsePaginatedResponse(d));
  },
  sendMessage: (id: number, content: string, msg_type: number | string = 1, source?: string) => {
    if (useMock && isMockSession(id)) {
      return Promise.resolve({});
    }
    return apiPost(`/sessions/${id}/messages`, {
      content,
      msg_type,
      ...(source ? { source } : {}),
    });
  },
  updateSessionUser: (id: number, data: Record<string, unknown>) =>
    apiPut(`/sessions/${id}/user`, data),
  /** 查看会话访客手机号/微信号明文；每次调用均由后端记录审计日志 */
  revealPii: (id: number, type: 'phone' | 'weixin') =>
    apiPost<{ type: 'phone' | 'weixin'; value: string }>(`/sessions/${id}/pii-reveal`, { type }),
  updateAiStatus: (id: number, aiStatus: 0 | 1) =>
    apiPut(`/sessions/${id}/ai-status`, { ai_status: aiStatus }),
  /** 按用户批量修改 AI 状态 / 接待客服，作用于用户进行中的会话，逐个处理并返回失败明细 */
  batchUpdate: (data: BatchUpdateSessionsParams) =>
    apiPost<BatchUpdateSessionsResult>('/sessions/batch-update', data),
  /** 重置会话未读：传当前已读到的最大消息 ID */
  read: (id: number, msgId: number | string) => apiPut(`/sessions/${id}/read`, { msg_id: msgId }),
  /**
   * 撤回消息，msgId 传 messages.id（非 platform_msg_id），平台侧标识由服务端自行读取。
   * 目前仅小红书渠道支持，其他渠道返回 422。
   */
  recallMessage: (id: number, msgId: number | string) =>
    apiPost<RecallMessageResponse>(`/sessions/${id}/messages/${msgId}/recall`),
  /**
   * 坐席会话看板 - 客服列表（前端轮询）：客服基础信息与名下会话数，不含会话数据，按客服分页。
   * 必须带 only_agent=1，不带时后端按拆分前的旧格式返回（仅兼容旧前端）。
   * params 里的筛选条件只影响 list[].total 的统计口径。
   */
  agentBoardAgents: (kfId: AgentBoardKfId, params: Record<string, unknown> = {}) =>
    apiGet<AgentBoardAgentsResponse>('/sessions/agent-board', {
      ...params,
      kf_id: kfId,
      only_agent: 1,
    }),
  /**
   * 坐席会话看板 - 指定客服的会话（前端轮询）：按回复类型分组（公海按留资分组），不分页，最多 5000 个会话。
   * kfId 只支持客服 ID 与 0（公海）；params 为筛选条件，口径与 GET /sessions 一致，is_kf 会被忽略。
   */
  agentBoardSessions: (kfId: number, params: Record<string, unknown> = {}) =>
    apiGet<AgentBoardSessionsResponse>('/sessions/agent-board/sessions', {
      ...params,
      kf_id: kfId,
    }),
  /**
   * 转接客服下拉：已排除当前接待客服，在线的排前面，最多 500 条。
   * 接口 keyword 会同时匹配昵称 / 用户名 / 工号，页面只按昵称搜，故不传、由前端本地过滤。
   */
  transferAgents: (id: number) =>
    apiGet(`/sessions/${id}/transfer-agents`).then((d) => parseListResponse<TransferAgent>(d)),
  /** 提交转接：会话归属改为目标客服，并插入 2 条仅坐席端可见的系统消息 */
  transfer: (id: number, data: SessionTransferPayload) =>
    apiPost<SessionTransferResult>(`/sessions/${id}/transfer`, data),
};

/** 快捷回复 - 访客会话页面使用 */
export const quickReplyApi = {
  list: (channel = 'public') =>
    apiGet('/app-config/quick-reply', { channel }).then((d) => parseListResponse(d)),
  save: (data: Record<string, unknown>) => apiPost('/app-config/quick-reply', data),
};
